import { Injectable } from '@angular/core';
import { Subject, of } from 'rxjs';
import { switchMap, catchError, map, filter } from 'rxjs/operators';
import {
  SpinnerV2Service,
  objectsHaveAtMostOneDifference,
  StorageV2Service,
  StoreV2,
  deepEqual,
  GridState,
} from '@schenck/rotec-components';
import { SettingsService } from './settings.service';
import { SearchKeys } from 'src/utils/enums/search.enum';
import { SearchParams, SearchSortParam } from 'src/utils/interfaces/search.interface';
import { SearchApi } from 'src/api/search.api';

export type SearchState = GridState<RotorSearchResult, SearchKeys>;

export interface RotorSearchResult {
  id: string;
  typeName: string;
  rotorId: string;
  batchId: string;
  timestamp: string;
  machineSerial: string;
  inTolerance: boolean;
}

@Injectable()
export class RotorSearchService extends StoreV2<SearchState> {
  private _search$ = new Subject<SearchParams>();

  constructor(
    private searchApi: SearchApi,
    private loadingService: SpinnerV2Service,
    private settingsService: SettingsService,
    storageService: StorageV2Service
  ) {
    super(
      {
        page: 0,
        pageSize: 50,
        fulltext: '',
        filter: [],
        sort: [],
        results: [],
        total: 0,
      },
      'RotorSearchService',
      { name: 'rotorSearchService', storageService }
    );

    this.setupSearchListener();
    // Do new search with potentially saved parameters
    this.state.results = [];
    this.state.total = 0;
    this._search$.next({
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
    });
    this.setupSettingsListener();
  }

  /**
   * Invokes a search.
   * All parameters that are not present will be reused.
   */
  search(params: Partial<SearchParams>) {
    const currSearchParams: SearchParams = {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
    };
    const newSearchParams: SearchParams = {
      ...currSearchParams,
      ...params,
    };

    // If only sort has changed, do sorting in frontend, else call backend
    if (
      objectsHaveAtMostOneDifference(newSearchParams, currSearchParams, 'sort') &&
      !deepEqual(currSearchParams.sort, newSearchParams.sort)
    ) {
      this.patchState('sorted', {
        results: sortResults(this.state.results, params.sort),
        ...newSearchParams,
      });
    } else {
      this.patchState('updated params', newSearchParams);
      this._search$.next(newSearchParams);
    }
  }

  private setupSettingsListener() {
    this.settingsService.settings$
      .pipe(
        map((settings) => settings.searchSettings.pageSize),
        filter((pageSize) => pageSize && pageSize !== this.state.pageSize)
      )
      .subscribe((pageSize) => {
        this.search({ pageSize });
      });
  }

  private setupSearchListener() {
    this._search$
      .pipe(
        switchMap((params) => {
          this.setLoading(true);
          return this.searchApi
            .search(params)
            .pipe(catchError(() => of({ total: 0, results: [] })));
        })
      )
      .subscribe((results) => {
        this.setLoading(false);
        this.patchState('got backend results', {
          total: results.total,
          results: sortResults(results.results, this.state.sort),
        });
      });
  }

  private setLoading(loading: boolean) {
    this.loadingService.setLoading('search', loading);
  }
}

/**
 * Sorts the search results by given parameters.
 * If no sort parameters are given, the default sort by timestamp is used
 */
export function sortResults(results: RotorSearchResult[] = [], sort: SearchSortParam[] = []) {
  if (sort.length === 0) {
    sort = [{ key: SearchKeys.timestamp, direction: 'desc' }];
  }

  return [...results].sort((resultA: RotorSearchResult, resultB: RotorSearchResult) => {
    for (let sortParam of sort) {
      const compareResult = searchResultComparator(resultA, resultB, sortParam);
      if (compareResult !== 0) {
        return compareResult;
      }
    }
    return 0;
  });
}

function searchResultComparator(
  resultA: RotorSearchResult,
  resultB: RotorSearchResult,
  sortBy: SearchSortParam
) {
  const sortDir = sortBy.direction === 'desc' ? -1 : 1;

  switch (sortBy.key) {
    case SearchKeys.batchId:
    case SearchKeys.machineSerial:
    case SearchKeys.rotorId:
    case SearchKeys.typeName:
    case SearchKeys.machineName:
    case SearchKeys.operatorName:
    case SearchKeys.projectNo:
      return (
        (resultA[sortBy.key] || '')
          .toLowerCase()
          .localeCompare((resultB[sortBy.key] || '').toLowerCase()) * sortDir
      );
    case SearchKeys.inTolerance:
      const onlyA: number = +(resultA.inTolerance && !resultB.inTolerance);
      const onlyB: number = +(!resultA.inTolerance && resultB.inTolerance) * -1;
      return (onlyA + onlyB) * sortDir;
    case SearchKeys.timestamp:
      return (
        (new Date(resultA.timestamp).getTime() - new Date(resultB.timestamp).getTime()) * sortDir
      );
    default:
      return 0; // should never get to this
  }
}
